/**
 * Client for the `users-resolve` edge function.
 *
 * The browser cannot call the Powtoon public user endpoint directly (CORS and
 * the org header), so ids are resolved in one batched round trip. Resolution is
 * best-effort: unresolvable ids are simply absent from the returned map.
 */
import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("powtoon.users");
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/users-resolve`;

export type PowtoonUserSummary = { name: string; thumbUrl: string | null };

export async function resolveUsers(
  ids: (string | number)[],
): Promise<Record<string, PowtoonUserSummary>> {
  const unique = [...new Set(ids.map((v) => String(v)).filter(Boolean))];
  if (unique.length === 0) return {};

  const token = typeof window === "undefined" ? null : window.sessionStorage.getItem(TOKEN_KEY);
  if (!token) return {};
  const org = getOrgDomain();
  const correlationId = nextCorrelationId();

  try {
    const res = await fetch(appendOp(ENDPOINT, "resolve-users"), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: SUPABASE_KEY,
        authorization: `Bearer ${SUPABASE_KEY}`,
        "x-powtoon-token": token,
        ...(org ? { "x-powtoon-org": org } : {}),
        [CORRELATION_HEADER]: correlationId,
      },
      body: JSON.stringify({ ids: unique }),
    });
    if (!res.ok) {
      logger.warn("user resolve failed", { status: res.status, correlationId });
      return {};
    }
    const data = (await res.json()) as { users?: Record<string, PowtoonUserSummary> };
    return data.users ?? {};
  } catch (err) {
    logger.warn("user resolve error", { correlationId, error: String(err) });
    return {};
  }
}
